import React from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import {
  ContentActions,
  APPROVE_ACTION_COLORS,
  REJECT_ACTION_COLORS,
} from "@/components/ui/ContentActions";
import { Icon } from "@/components/ui/Icon";
import { Feather } from "@expo/vector-icons";
import type { LegalRepresentative } from "@/lib/api/review";
import {
  ReviewField,
  FieldRow,
  Divider,
  FieldDecisionActions,
  type FieldDecision,
  type RejectFieldMeta,
  type FieldRejectionData,
  type EditFieldMeta,
  type FieldEditData,
} from "./Shared";
import { FilePreview } from "@/components/ui/FilePreview";

interface LegalRepSectionProps {
  legalRep: LegalRepresentative | undefined;
  fieldDecisions: Record<string, FieldDecision>;
  fieldRejectionData: Record<string, FieldRejectionData>;
  onDecide: (fieldKey: string, decision: FieldDecision) => void;
  onRejectField: (fieldKey: string, meta: RejectFieldMeta) => void;
  onUndoDecision: (fieldKey: string) => void;
  onShowReason: (fieldKey: string) => void;
  fieldEditData: Record<string, FieldEditData>;
  onEditField: (fieldKey: string, meta: EditFieldMeta) => void;
  onShowEdit: (fieldKey: string) => void;
  t: ReturnType<typeof import("@/context/I18nContext").useI18n>["t"];
  applicationId: string;
}

export function LegalRepSection({
  legalRep,
  fieldDecisions,
  fieldRejectionData,
  onDecide,
  onRejectField,
  onUndoDecision,
  onShowReason,
  fieldEditData: _fieldEditData,
  onEditField: _onEditField,
  onShowEdit,
  applicationId,
  t,
}: LegalRepSectionProps) {
  const viewOnly = t("review.review.viewOnly");

  return (
    <View className="items-start w-full">
      {/* Card header */}
      <View className="px-6 py-5 border-b border-stroke-50 items-start w-full">
        <AppText weight="semibold" className="text-lg text-dark-text-50">
          {t("review.application.legalRepresentative.title")}
        </AppText>
      </View>

      {/* Card body */}
      <View className="px-8 py-6 w-full">
        <View className="gap-0">
          {/* Full legal name (Chinese) + (English) */}
          <FieldRow>
            <ReviewField
              label={t("review.application.legalRepresentative.fields.fullNameChinese")}
              viewOnlyLabel={viewOnly}
            >
              <AppText weight="medium" className="text-lg text-dark-text-50">
                {legalRep?.fullNameChinese}
              </AppText>
            </ReviewField>
            <ReviewField
              label={t("review.application.legalRepresentative.fields.fullNameEnglish")}
              viewOnlyLabel={viewOnly}
            >
              <AppText weight="medium" className="text-lg text-dark-text-50">
                {legalRep?.fullNameEnglish}
              </AppText>
            </ReviewField>
          </FieldRow>

          <Divider />

          {/* Nationality  */}
          <FieldRow>
            <ReviewField
              label={t("review.application.legalRepresentative.fields.nationality")}
              viewOnlyLabel={viewOnly}
            >
              <AppText weight="medium" className="text-lg text-dark-text-50">
                {legalRep?.nationality}
              </AppText>
            </ReviewField>
            {/* empty right column, matching Figma half-width layout */}
            <View className="flex-1" />
          </FieldRow>

          <Divider />

          {/* Role + Department */}
          <FieldRow>
            <ReviewField
              label={t("review.application.legalRepresentative.fields.role")}
              viewOnlyLabel={viewOnly}
            >
              <AppText weight="medium" className="text-lg text-dark-text-50">
                {legalRep?.role}
              </AppText>
            </ReviewField>
            <ReviewField
              label={t("review.application.legalRepresentative.fields.department")}
              viewOnlyLabel={viewOnly}
            >
              <AppText weight="medium" className="text-lg text-dark-text-50">
                {legalRep?.department}
              </AppText>
            </ReviewField>
          </FieldRow>

          <Divider />

          {/* Phone number + Business email */}
          <FieldRow>
            <ReviewField
              label={t("review.application.legalRepresentative.fields.phoneNumber")}
              viewOnlyLabel={viewOnly}
            >
              <AppText weight="medium" className="text-lg text-dark-text-50">
                {legalRep?.phone}
              </AppText>
            </ReviewField>
            <ReviewField
              label={t("review.application.legalRepresentative.fields.businessEmail")}
              viewOnlyLabel={viewOnly}
            >
              <AppText weight="medium" className="text-lg text-dark-text-50">
                {legalRep?.email}
              </AppText>
            </ReviewField>
          </FieldRow>

          <Divider />

          {/* Legal representative ID — full width with doc image + Approve/Reject */}
          <View className="gap-3 w-full items-start">
            <AppText weight="semibold" className="text-base text-supporting-text-40">
              {t("review.application.legalRepresentative.fields.legalId")}
            </AppText>
            <AppText weight="medium" className="text-lg text-dark-text-50">
              {legalRep?.idType ?? t("review.application.legalRepresentative.passport")}
            </AppText>

            {/* Document image or placeholder */}
            {legalRep?.idDocumentUrl ? (
              <FilePreview
                className="max-w-[320px]"
                fileKey={legalRep?.idDocumentUrl}
                isVendor={false}
                applicationId={applicationId}
              />
            ) : (
              <View className="max-w-[320px] h-[200px] bg-white rounded-lg border border-stroke-30 items-center justify-center">
                <AppText className="text-sm text-dark-text-20 text-center">
                  {t("review.application.legalRepresentative.notUploaded")}
                </AppText>
              </View>
            )}

            {/* Field-level audit actions */}
            {fieldDecisions["id_type"] ? (
              <FieldDecisionActions
                fieldKey="id_type"
                decision={fieldDecisions["id_type"]}
                onUndoDecision={onUndoDecision}
                onShowReason={onShowReason}
                onShowEdit={onShowEdit}
                t={t}
              />
            ) : (
              <ContentActions
                actions={[
                  {
                    label: "review.application.approve",
                    onPress: () => onDecide("id_type", "approve"),
                    icon: (
                      <Icon as={Feather} name="check-circle" size={14} className="text-green-50" />
                    ),
                    ...APPROVE_ACTION_COLORS,
                  },
                  {
                    label: "review.application.reject",
                    onPress: () =>
                      onRejectField("id_type", {
                        label: t("review.application.legalRepresentative.fields.legalId"),
                        value:
                          legalRep?.idType ?? t("review.application.legalRepresentative.passport"),
                        fileKey: legalRep?.idDocumentUrl,
                      }),
                    icon: <Icon as={Feather} name="x" size={14} className="text-error" />,
                    ...REJECT_ACTION_COLORS,
                  },
                ]}
              />
            )}
          </View>

          <Divider />

          {/* WeChat QR Code */}
          <ReviewField
            label={t("review.application.legalRepresentative.fields.wechatQr")}
            viewOnlyLabel={viewOnly}
          >
            {legalRep?.wechatQrUrl ? (
              <FilePreview
                className="max-w-[320px]"
                fileKey={legalRep?.wechatQrUrl}
                isVendor={false}
                applicationId={applicationId}
              />
            ) : (
              <AppText className="text-base text-dark-text-20 italic">
                {t("review.application.legalRepresentative.notUploaded")}
              </AppText>
            )}
          </ReviewField>
        </View>
      </View>
    </View>
  );
}
